import { isPlatformBrowser } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import { textVariants } from '@freelancer/ui/text';
import type { Subscription } from 'rxjs';
import { fromEvent } from 'rxjs';
import { IconColor, IconModule, IconSize } from '../icon';
import { FontColor, FontWeight } from '../text';
import type { ProgressColorMap, ProgressMilestone } from './progress-bar.types';
import {
  ProgressBackground,
  ProgressColor,
  ProgressOrientation,
  ProgressSize,
} from './progress-bar.types';

@Component({
  standalone: true,
  imports: [IconModule],
  selector: 'fl-progress-bar',
  template: `
    <div
      class="BarContainer"
      [attr.data-orientation]="orientation"
      [attr.data-orientation-tablet]="orientationTablet"
      [attr.data-orientation-desktop]="orientationDesktop"
      [attr.data-size]="size"
      [attr.data-size-tablet]="sizeTablet"
      [attr.data-size-desktop]="sizeDesktop"
      [attr.data-background-color]="backgroundColor"
      [attr.data-rounded-corners]="roundedCorners"
      [attr.data-has-checkpoints]="hasCheckpoints"
    >
      <div
        class="ProgressHighlight ProgressHighlight--secondary"
        [attr.data-orientation]="orientation"
        [attr.data-orientation-tablet]="orientationTablet"
        [attr.data-orientation-desktop]="orientationDesktop"
      >
        <div
          class="ProgressHighlight-inner"
          [attr.data-orientation]="orientation"
          [attr.data-orientation-tablet]="orientationTablet"
          [attr.data-orientation-desktop]="orientationDesktop"
          [attr.data-has-checkpoints]="hasCheckpoints"
        >
          <div
            #secondaryProgressHighlight
            class="ProgressHighlight-inner-spacer"
            [attr.data-orientation]="orientation"
            [attr.data-orientation-tablet]="orientationTablet"
            [attr.data-orientation-desktop]="orientationDesktop"
            [attr.data-has-checkpoints]="hasCheckpoints"
            [attr.data-size]="size"
            [attr.data-size-tablet]="sizeTablet"
            [attr.data-size-desktop]="sizeDesktop"
          ></div>
        </div>
      </div>
      @if (checkpoints) {
        <div
          class="milestoneElementsContainer CheckpointsContainer"
          [attr.data-orientation]="orientation"
          [attr.data-orientation-tablet]="orientationTablet"
          [attr.data-orientation-desktop]="orientationDesktop"
        >
          <div
            class="CheckpointsContainer-inner"
            [attr.data-orientation]="orientation"
            [attr.data-orientation-tablet]="orientationTablet"
            [attr.data-orientation-desktop]="orientationDesktop"
          >
            @for (
              milestone of checkpoints;
              track trackByMilestoneText($index, milestone)
            ) {
              <div
                #milestone
                class="CheckpointsContainer-milestone"
                [attr.data-progress]="milestone.progress"
              >
                <div
                  class="CheckpointsContainer-milestone-dot"
                  [attr.data-orientation]="orientation"
                  [attr.data-orientation-tablet]="orientationTablet"
                  [attr.data-orientation-desktop]="orientationDesktop"
                  [attr.data-progress]="milestone.progress"
                ></div>
              </div>
            }
          </div>
        </div>
      }
      <div
        class="ProgressHighlight"
        [attr.data-orientation]="orientation"
        [attr.data-orientation-tablet]="orientationTablet"
        [attr.data-orientation-desktop]="orientationDesktop"
      >
        <div
          class="ProgressHighlight-inner"
          [attr.data-orientation]="orientation"
          [attr.data-orientation-tablet]="orientationTablet"
          [attr.data-orientation-desktop]="orientationDesktop"
          [attr.data-has-checkpoints]="hasCheckpoints"
        >
          <div
            #progressHighlight
            class="ProgressHighlight-inner-spacer"
            [attr.data-orientation]="orientation"
            [attr.data-orientation-tablet]="orientationTablet"
            [attr.data-orientation-desktop]="orientationDesktop"
            [attr.data-size]="size"
            [attr.data-size-tablet]="sizeTablet"
            [attr.data-size-desktop]="sizeDesktop"
            [attr.data-has-checkpoints]="hasCheckpoints"
          ></div>
        </div>
      </div>
      @if (showProgressMarker) {
        <div
          class="CheckpointsContainer"
          [attr.data-orientation]="orientation"
          [attr.data-orientation-tablet]="orientationTablet"
          [attr.data-orientation-desktop]="orientationDesktop"
        >
          <div
            class="CheckpointsContainer-inner"
            [attr.data-orientation]="orientation"
            [attr.data-orientation-tablet]="orientationTablet"
            [attr.data-orientation-desktop]="orientationDesktop"
          >
            <div
              #marker
              class="CheckpointsContainer-inner-marker"
              [attr.data-orientation]="orientation"
              [attr.data-orientation-tablet]="orientationTablet"
              [attr.data-orientation-desktop]="orientationDesktop"
            >
              <div
                #triangle
                class="CheckpointsContainer-inner-marker-triangle"
                [attr.data-orientation]="orientation"
                [attr.data-orientation-tablet]="orientationTablet"
                [attr.data-orientation-desktop]="orientationDesktop"
              ></div>
              <fl-icon
                class="Logo"
                [name]="'ui-bird'"
                [size]="IconSize.XSMALL"
                [color]="IconColor.INHERIT"
              ></fl-icon>
            </div>
          </div>
        </div>
      }
    </div>
    @if (checkpoints) {
      <div
        class="CheckpointsContainer MilestoneLabelsContainer"
        [attr.data-orientation]="orientation"
        [attr.data-orientation-tablet]="orientationTablet"
        [attr.data-orientation-desktop]="orientationDesktop"
      >
        @for (
          milestone of checkpoints;
          track trackByMilestoneText($index, milestone)
        ) {
          <div
            #milestone
            class="CheckpointsContainer-milestone"
            [attr.data-progress]="milestone.progress"
          >
            <p
              [class]="labelCls"
              [attr.data-orientation]="orientation"
              [attr.data-orientation-tablet]="orientationTablet"
              [attr.data-orientation-desktop]="orientationDesktop"
            >
              {{ milestone.text }}
            </p>
          </div>
        }
      </div>
    }
  `,
  styleUrls: ['./progress-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressBarComponent
  implements AfterViewInit, OnChanges, OnDestroy, OnInit
{
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(Renderer2);
  private timeUtils = inject(TimeUtils);

  IconColor = IconColor;
  IconSize = IconSize;

  @HostBinding('attr.data-progress-percentage')
  @Input()
  progressPercentage: number;

  @Input() secondaryProgressPercentage?: number;

  /**
   * Progress bar colour. Default is blue.
   */
  @Input() color: ProgressColor = ProgressColor.DEFAULT;

  @HostBinding('attr.data-background-color')
  @Input()
  backgroundColor: ProgressBackground = ProgressBackground.DEFAULT;

  @HostBinding('attr.data-size')
  @Input()
  size: ProgressSize = ProgressSize.SMALL;

  @HostBinding('attr.data-size-tablet')
  @Input()
  sizeTablet?: ProgressSize;

  @HostBinding('attr.data-size-desktop')
  @Input()
  sizeDesktop?: ProgressSize;

  @HostBinding('attr.data-orientation')
  @Input()
  orientation: ProgressOrientation = ProgressOrientation.HORIZONTAL;

  @HostBinding('attr.data-orientation-tablet')
  @Input()
  orientationTablet?: ProgressOrientation;

  @HostBinding('attr.data-orientation-desktop')
  @Input()
  orientationDesktop?: ProgressOrientation;

  /**
   * Set the input to false if you need the progress bar to be edge to edge
   * within pages, modals, etc. (i.e take up the full width of the container)
   */
  @HostBinding('attr.data-rounded-corners')
  @Input()
  roundedCorners = true;

  /**
   * Progress bar's aria label for screen readers
   */
  @HostBinding('attr.aria-label')
  @Input()
  label: string;

  @Input()
  labelColor: FontColor = FontColor.FOREGROUND;

  @Input()
  checkpoints: readonly ProgressMilestone[];

  /**
   * Show progress marker with Freelancer logo and triangle marker
   */
  @Input()
  showProgressMarker = false;

  hasCheckpoints = false;

  @HostBinding('attr.role')
  role = 'progressbar';

  @HostBinding('attr.aria-valuemin')
  valueMin = '0';

  @HostBinding('attr.aria-valuemax')
  valueMax = '100';

  @HostBinding('attr.aria-valuenow')
  get progressValue(): number {
    return this.progressPercentage;
  }

  @ViewChild('progressHighlight', { read: ElementRef, static: true })
  progressHighlight: ElementRef<HTMLDivElement>;

  @ViewChild('secondaryProgressHighlight', { read: ElementRef, static: true })
  secondaryProgressHighlight: ElementRef<HTMLDivElement>;

  @ViewChild('marker', { read: ElementRef })
  marker: ElementRef<HTMLDivElement>;

  @ViewChild('triangle', { read: ElementRef })
  triangle: ElementRef<HTMLDivElement>;

  @ViewChildren('milestone', { read: ElementRef })
  milestoneElements: QueryList<ElementRef>;

  // Default color map for blue progress bar
  readonly blueColorMap: ProgressColorMap = {
    solid: '#1772eb', // $blue-dark
    gradientStart: '#589af0', // $blue
    gradientEnd: '#082e60', // $blue-xxdark,
  };

  readonly lightBlueColorMap: ProgressColorMap = {
    solid: '#139FF0', // $blue-dark
    gradientStart: '#139FF0', // $blue
    gradientEnd: '#30DBE3', // $blue-xxdark
  };
  // Color map for green progress bar
  readonly greenColorMap: ProgressColorMap = {
    solid: '#5dc26a', // $green
    gradientStart: '#4fb55d', // $green-dark
    gradientEnd: '#95de90', // $green-light
  };

  // Color map for pink progress bar
  readonly pinkColorMap: ProgressColorMap = {
    solid: '#f20091', // $pink
    gradientStart: '#115cc0', // $blue-xdark
    gradientEnd: '#F20091', // $pink
  };

  // Color map for yellow progress bar
  readonly yellowColorMap: ProgressColorMap = {
    solid: '#ffd747', // $yellow
    gradientStart: '#e1b000', // $yellow-dark
    gradientEnd: '#fff3cb', // $yellow-light
  };

  // Color map for orange progress bar
  readonly orangeColorMap: ProgressColorMap = {
    solid: '#fc8c14', // $orange
    gradientStart: '#f77d0e', // $orange-dark
    gradientEnd: '#ffa32d', // $orange-light
  };

  // Color map for red progress bar
  readonly redColorMap: ProgressColorMap = {
    solid: '#eb3730', // $red-400
    gradientStart: '#e02a28', // $red-500
    gradientEnd: '#f84438', // $red-300
  };

  // Solid fill and gradient colour values for progress bar
  colorMap = this.blueColorMap;

  private windowResize?: Subscription;

  labelCls: string;

  ngAfterViewInit(): void {
    this.updateView();
    this.initializeCheckpoints();

    if (isPlatformBrowser(this.platformId)) {
      this.windowResize = fromEvent(window, 'resize')
        .pipe(this.timeUtils.rxDebounceTime(500))
        .subscribe(() => {
          this.initializeCheckpoints();
          this.updateView();
        });
    }
  }

  initializeCheckpoints(): void {
    const orientation = this.getViewportOrientation();

    this.milestoneElements.forEach(milestoneElement => {
      const progress = `${(
        milestoneElement.nativeElement as HTMLElement
      ).getAttribute('data-progress')}%`;
      milestoneElement.nativeElement.style.left = progress;
      if (orientation === 'width') {
        this.renderer.removeStyle(milestoneElement.nativeElement, 'top');

        milestoneElement.nativeElement.style.left = progress;
      } else {
        this.renderer.removeStyle(milestoneElement.nativeElement, 'left');
        milestoneElement.nativeElement.style.top = progress;
      }
    });
  }

  getScreenSize(): 'desktop' | 'tablet' | 'mobile' | undefined {
    if (isPlatformBrowser(this.platformId)) {
      return window.innerWidth >=
        parseInt(FreelancerBreakpointValues.DESKTOP_SMALL, 10)
        ? 'desktop'
        : window.innerWidth <
              parseInt(FreelancerBreakpointValues.DESKTOP_SMALL, 10) &&
            window.innerWidth >= parseInt(FreelancerBreakpointValues.TABLET, 10)
          ? 'tablet'
          : 'mobile';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'orientation' in changes ||
      'orientationTablet' in changes ||
      'orientationDesktop' in changes ||
      'checkpoints' in changes
    ) {
      this.cleanupView();
    }

    if (
      'size' in changes ||
      'sizeTablet' in changes ||
      'sizeDesktop' in changes
    ) {
      if (!this.sizeTablet) {
        this.sizeTablet = this.size;
      }
      if (!this.sizeDesktop) {
        this.sizeDesktop = this.size;
      }
    }

    if ('checkpoints' in changes) {
      this.hasCheckpoints = !!this.checkpoints;
    }

    if ('color' in changes && this.color) {
      switch (this.color) {
        case ProgressColor.SUCCESS:
          this.colorMap = this.greenColorMap;
          break;
        case ProgressColor.PINK:
          this.colorMap = this.pinkColorMap;
          break;
        case ProgressColor.DEFAULT_LIGHT:
          this.colorMap = this.lightBlueColorMap;
          break;
        case ProgressColor.YELLOW:
          this.colorMap = this.yellowColorMap;
          break;
        case ProgressColor.ORANGE:
          this.colorMap = this.orangeColorMap;
          break;
        case ProgressColor.RED:
          this.colorMap = this.redColorMap;
          break;
        default:
          this.colorMap = this.blueColorMap;
      }

      this.computeClasses();
    }

    this.updateView();
  }

  ngOnInit(): void {
    this.computeClasses();
  }

  updateView(): void {
    const progress = Math.min(Math.max(this.progressPercentage / 100, 0), 1);
    const secondaryProgress = Math.min(
      Math.max((this.secondaryProgressPercentage || 0) / 100, 0),
      1,
    );
    const progressElement = this.progressHighlight.nativeElement;
    const secondaryProgressElement =
      this.secondaryProgressHighlight?.nativeElement;
    const orientation = this.getViewportOrientation();

    this.updateHighlightStyle(progressElement, orientation, progress);

    if (secondaryProgressElement) {
      this.updateHighlightStyle(
        secondaryProgressElement,
        orientation,
        secondaryProgress,
      );
    }

    if (this.marker) {
      if (orientation === 'width') {
        this.renderer.removeStyle(this.marker.nativeElement, 'top');

        this.marker.nativeElement.style.left = `${progress * 100}%`;
      } else {
        this.renderer.removeStyle(this.marker.nativeElement, 'left');
        this.marker.nativeElement.style.top = `${progress * 100}%`;
      }
    }
  }

  updateHighlightStyle(
    element: HTMLDivElement,
    orientation: 'width' | 'height',
    progress: number,
  ): void {
    const style = this.checkpoints
      ? `calc(${progress * 100}% + ${this.getViewportBarThickness()}px)`
      : `${progress * 100}%`;
    this.renderer.setStyle(element, orientation, style);

    if (orientation === 'height') {
      this.renderer.removeStyle(element, 'width');
    }

    if (orientation === 'width') {
      this.renderer.removeStyle(element, 'height');
    }

    element.style.background = this.colorMap.solid;
    if (this.triangle) {
      this.triangle.nativeElement.style.background = this.colorMap.solid;
    }
  }

  getViewportOrientation(): 'width' | 'height' {
    if (this.getScreenSize() === 'desktop' && this.orientationDesktop) {
      return this.getOrientation(this.orientationDesktop);
    }

    if (
      (this.getScreenSize() === 'tablet' && this.orientationTablet) ||
      (this.getScreenSize() === 'desktop' &&
        this.orientationTablet &&
        !this.orientationDesktop)
    ) {
      return this.getOrientation(this.orientationTablet);
    }

    return this.getOrientation(this.orientation);
  }

  getViewportBarThickness(): number {
    if (this.getScreenSize() === 'desktop' && this.sizeDesktop) {
      return this.getBarThickness(this.sizeDesktop);
    }

    if (this.getScreenSize() === 'tablet' && this.sizeTablet) {
      return this.getBarThickness(this.sizeTablet);
    }

    return this.getBarThickness(this.size);
  }

  getBarThickness(size: ProgressSize): number {
    switch (size) {
      case ProgressSize.XSMALL:
        return 2;
      case ProgressSize.SMALL:
        return 6;
      case ProgressSize.LARGE:
        return 10;
      case ProgressSize.XLARGE:
        return 14;
      case ProgressSize.XXLARGE:
        return 18;
      default:
        return 6;
    }
  }

  getOrientation(orientation: ProgressOrientation): 'width' | 'height' {
    return orientation === ProgressOrientation.HORIZONTAL ? 'width' : 'height';
  }

  getViewportGradientOrientation(): 'right' | 'bottom' {
    if (this.getScreenSize() === 'desktop' && this.orientationDesktop) {
      return this.getGradientOrientation(this.orientationDesktop);
    }

    if (this.getScreenSize() === 'tablet' && this.orientationTablet) {
      return this.getGradientOrientation(this.orientationTablet);
    }

    return this.getGradientOrientation(this.orientation);
  }

  getGradientOrientation(orientation: ProgressOrientation): 'right' | 'bottom' {
    return orientation === ProgressOrientation.HORIZONTAL ? 'right' : 'bottom';
  }

  cleanupView(): void {
    const progressStyle = this.progressHighlight.nativeElement;

    this.renderer.removeStyle(progressStyle, 'width');
    this.renderer.removeStyle(progressStyle, 'height');
  }

  ngOnDestroy(): void {
    this.windowResize?.unsubscribe();
  }

  trackByMilestoneText(_: number, milestone: ProgressMilestone): string {
    return milestone.text;
  }

  private computeClasses(): void {
    this.labelCls = textVariants({
      class: 'CheckpointsContainer-milestone-label',
      color: this.labelColor,
      weight: FontWeight.MEDIUM,
    });
  }
}
